import type { Observable, Subscription } from 'rxjs';

/**
 *
 * transport 무관(WS 전용 아님): subscribe()가 Observable, request()가 snapshot이면 됨.
 * 우리는 subscribe()를 EventSource(SSE) 기반 Observable로, request()를 GET /snapshot로 채운다.
 *
 * 원칙(= 우리 SSE 설계와 동일): 이벤트(delta)는 안 믿고 서버 snapshot이 source of truth.
 * - 'ready'(재)연결 신호 → revalidate()로 snapshot 재조회
 * - applyEvent가 'revalidate' 반환 → 역시 snapshot 재조회
 * - generation 카운터로 stop 이후의 stale 응답 무시
 */

// 원본의 `@affine/realtime` 내부 타입을 inline
export type RealtimeSubscriptionReady = { type: 'ready' };

export type RealtimeLiveQueryEventResult = 'applied' | 'revalidate';

export type RealtimeLiveQueryOptions<TSnapshot, TEvent extends object> = {
  request: (signal: AbortSignal) => Promise<TSnapshot>;
  subscribe: () => Observable<TEvent | RealtimeSubscriptionReady>;
  applySnapshot: (snapshot: TSnapshot) => void;
  applyEvent?: (event: TEvent) => RealtimeLiveQueryEventResult;
  onError?: (error: unknown) => void;
};

function isReadyEvent<TEvent extends object>(
  event: TEvent | RealtimeSubscriptionReady,
): event is RealtimeSubscriptionReady {
  return 'type' in event && event.type === 'ready';
}

export class RealtimeLiveQuery<TSnapshot, TEvent extends object> {
  private subscription?: Subscription;
  private requestController?: AbortController;
  private generation = 0;
  private started = false;

  constructor(private readonly options: RealtimeLiveQueryOptions<TSnapshot, TEvent>) {}

  start() {
    this.stop();
    this.started = true;
    const generation = this.generation;
    this.subscription = this.options.subscribe().subscribe({
      next: (event) => {
        if (isReadyEvent(event)) {
          this.revalidate();
          return;
        }
        if (!this.options.applyEvent) {
          this.revalidate();
          return;
        }
        if (this.options.applyEvent(event) === 'revalidate') {
          this.revalidate();
        }
      },
      error: (error) => {
        if (this.generation === generation) {
          this.options.onError?.(error);
        }
      },
    });
  }

  revalidate() {
    if (!this.started) {
      return;
    }
    this.requestController?.abort();
    const controller = new AbortController();
    this.requestController = controller;
    const generation = this.generation;
    this.options.request(controller.signal).then(
      (snapshot) => {
        if (
          this.started &&
          this.generation === generation &&
          this.requestController === controller &&
          !controller.signal.aborted
        ) {
          this.options.applySnapshot(snapshot);
        }
      },
      (error) => {
        if (
          this.started &&
          this.generation === generation &&
          this.requestController === controller &&
          !controller.signal.aborted
        ) {
          this.options.onError?.(error);
        }
      },
    );
  }

  stop() {
    this.started = false;
    this.generation += 1;
    this.subscription?.unsubscribe();
    this.subscription = undefined;
    this.requestController?.abort();
    this.requestController = undefined;
  }

  dispose() {
    this.stop();
  }
}
